import React, { ReactElement } from 'react'
import styled from 'styled-components'
import AosStkBucketGoalTracker from '@ally/aos-stk-bucket-goal-tracker'
import { asCurrency } from '@ally/aos-utils'
import BucketImageMap from '@ally/bucket-image-map'
import {
  Accordion,
  AccordionItem,
  Box,
  Link,
  Icon,
  Space,
  TextBody,
} from '@ally/metronome-ui'
import { muiTarget } from '@ally/metronome-icons'
import { useWidget } from './WidgetContext'
import { formatDate } from '../../../utils'
import { TextBodyOverride } from '../../../Shared/MetronomeOverrides'

const List = styled.ul`
  padding: 0;
  margin: -10px 0;
`

const ListItem = styled.li<{ hasGoal: boolean }>`
  ${({ theme: { colors, space }, hasGoal }): string => `
    min-height: 50px;
    list-style: none;
    align-items: center;
    padding: ${space.xs} 0;
    border-bottom: 1px solid ${colors['slate-2']};

    ${hasGoal ? `` : `display: flex;`}

    &:last-child {
      padding-bottom: 0;
      border-bottom: none;
    }
  `};
`

const BucketName = styled(TextBodyOverride)`
  flex: 1;
  width: 145px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledAccordionWrapper = styled(Accordion)`
  ${({ theme: { space } }): string => `
    > div > div:nth-child(1) {
      padding: ${space.sm} 0;

      > div {
        display: grid;
      }
    }

    p {
      align-items: center;
    }
  `};
`

interface BucketItemProps {
  bucketBalance: number
  bucketIcon: ReactElement
  displayName: string | undefined
  hasGoal?: boolean
}

interface BucketItemGoalProps {
  bucketGoalAmount: number
  bucketGoalTargetDate: string
  monetaryGoalProgress: number
}

const BucketItem = ({
  bucketIcon,
  displayName,
  bucketBalance,
  hasGoal,
}: BucketItemProps): ReactElement => {
  return (
    <>
      <Space ml={hasGoal ? '' : 'md'} mr="sm">
        {bucketIcon}
      </Space>
      <Box display="flex" flex="1" justifyContent="space-between">
        <Space mr="sm">
          <BucketName
            tag="span"
            size="sm"
            weight="bold"
            color={hasGoal ? 'bluesuedeshoes' : 'slate-5'}
          >
            {displayName}
          </BucketName>
        </Space>
        <TextBodyOverride
          tag="span"
          size="sm"
          weight={hasGoal ? 'bold' : 'normal'}
          color={hasGoal ? 'bluesuedeshoes' : 'slate-5'}
        >
          {asCurrency(bucketBalance)}
        </TextBodyOverride>
      </Box>
    </>
  )
}

const BucketItemGoal = ({
  bucketGoalAmount,
  bucketGoalTargetDate,
  monetaryGoalProgress,
}: BucketItemGoalProps): ReactElement => {
  return (
    <Box pt="xxs" pl="14px">
      <Box>
        <Box display="flex">
          <Icon
            icon={muiTarget}
            size="md"
            mt="2px"
            mr="xs"
            fill="slate-4"
            ariaHidden
          />
          <Box flex="1">
            <AosStkBucketGoalTracker
              monetaryGoalProgress={monetaryGoalProgress}
              bucketGoalAmount={bucketGoalAmount}
              skinny
            />
          </Box>
        </Box>
      </Box>
      {bucketGoalTargetDate && (
        <Box pl="lg" mt="xxs">
          <TextBody tag="p" size="sm">
            <TextBody tag="span" size="sm" weight="bold">
              Target Date:
            </TextBody>{' '}
            {formatDate(bucketGoalTargetDate, 'MMM D, YYYY')}
          </TextBody>
        </Box>
      )}
    </Box>
  )
}

const Buckets: React.FC<{
  handleRef: (el: HTMLElement | null) => number | null
}> = ({ handleRef }): ReactElement => {
  const { activeAccountId, stkSummaryData } = useWidget()
  const hasBuckets = stkSummaryData && stkSummaryData.buckets.length > 1
  const slicedBuckets = stkSummaryData && stkSummaryData.buckets.slice(0, 4)
  const showMoreLink = stkSummaryData && stkSummaryData.buckets.length > 4

  return hasBuckets ? (
    <>
      <List tabIndex={-1} ref={handleRef} data-testid="stk-widget-buckets-list">
        {slicedBuckets?.map(bucket => {
          const {
            accountBucketId,
            bucketBalance,
            bucketCategoryId,
            bucketGoalAmount,
            bucketGoalTargetDate,
            displayName,
            monetaryGoalProgress,
          } = bucket
          const BucketIcon = BucketImageMap[bucketCategoryId].sm
          return (
            <ListItem key={accountBucketId} hasGoal={!!bucketGoalAmount}>
              {bucketGoalAmount ? (
                <StyledAccordionWrapper variant="CONTAINERLESS">
                  <AccordionItem
                    clickEventName={`STK Goals widget ${displayName}`}
                    key={accountBucketId}
                    title={
                      <BucketItem
                        bucketBalance={bucketBalance}
                        bucketIcon={<BucketIcon aria-hidden="true" />}
                        displayName={displayName}
                        hasGoal
                      />
                    }
                    content={{
                      variant: 'CONTAINERLESS',
                      payload: (
                        <BucketItemGoal
                          bucketGoalAmount={bucketGoalAmount}
                          bucketGoalTargetDate={bucketGoalTargetDate}
                          monetaryGoalProgress={monetaryGoalProgress}
                        />
                      ),
                    }}
                  />
                </StyledAccordionWrapper>
              ) : (
                <BucketItem
                  bucketBalance={bucketBalance}
                  bucketIcon={<BucketIcon aria-hidden="true" />}
                  displayName={displayName}
                />
              )}
            </ListItem>
          )
        })}
      </List>
      {showMoreLink && (
        <Box mt="md">
          <Link
            text="See all buckets"
            to={`bank/accounts/stk-osa/${activeAccountId}/buckets`}
            withAllyTM="STK See all buckets Widget"
          />
        </Box>
      )}
    </>
  ) : (
    <>
      <Space mb="xs">
        <TextBody tag="p" size="sm" tabIndex={-1} ref={handleRef}>
          You haven&apos;t set up buckets.
        </TextBody>
      </Space>
      <Link
        text="Set up buckets on your savings account."
        to={`bank/accounts/stk-osa/${activeAccountId}/buckets`}
        withAllyTM="STK Set up buckets on your savings account Setup widget"
      />
    </>
  )
}

export default Buckets
